import React, { useState } from 'react';
import { Button, Modal, Space, Typography, Tag, Tooltip } from 'antd';
import { CheckOutlined, CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons';

const { Text, Paragraph } = Typography;

/**
 * IntentConfirmationDialog Component
 * 
 * Handles medium confidence intent matches (75%-90%) by asking user for confirmation.
 * Implements auto-learning from user feedback to improve keyword matching over time.
 */
const IntentConfirmationDialog = ({
  visible,
  intentMatch,
  originalMessage,
  onConfirm,
  onDeny,
  onCancel,
  loading = false
}) => {
  const [feedback, setFeedback] = useState('');

  const handleConfirm = () => {
    onConfirm({
      confirmed: true,
      originalMessage,
      intentMatch,
      feedback: feedback.trim()
    });
  };

  const handleDeny = () => {
    onDeny({
      confirmed: false,
      originalMessage,
      intentMatch,
      feedback: feedback.trim()
    });
  };

  const getActionColor = (action) => {
    const colors = {
      'create': 'green',
      'edit': 'blue', 
      'delete': 'red',
      'analyze': 'purple',
      'view': 'cyan',
      'help': 'orange',
      'update': 'blue',
      'close': 'red'
    };
    return colors[action] || 'default';
  };

  const getAreaColor = (area) => {
    const colors = {
      'issue': 'red',
      'risk': 'orange',
      'control': 'blue',
      'incident': 'red',
      'compliance': 'green',
      'audit': 'purple',
      'kri': 'cyan',
      'action': 'gold'
    };
    return colors[area] || 'default';
  };

  const formatConfidence = (confidence) => {
    return `${Math.round(confidence * 100)}%`;
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.85) return '#52c41a'; // Green
    if (confidence >= 0.80) return '#faad14'; // Orange
    return '#ff4d4f'; // Red
  };

  if (!intentMatch) return null;

  return (
    <Modal
      title={
        <Space>
          <QuestionCircleOutlined style={{ color: '#faad14' }} />
          <span>Intent Confirmation</span>
          <Tooltip title="The system is learning to understand your requests better">
            <Tag color="blue" style={{ marginLeft: 8 }}>
              Smart Learning
            </Tag>
          </Tooltip>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button 
          key="deny" 
          onClick={handleDeny} 
          disabled={loading}
          icon={<CloseOutlined />}
        >
          No, that's not right
        </Button>,
        <Button 
          key="confirm" 
          type="primary" 
          onClick={handleConfirm}
          loading={loading}
          icon={<CheckOutlined />}
        >
          Yes, that's correct
        </Button>
      ]}
      width={600}
    >
      <div style={{ marginBottom: 16 }}>
        <Paragraph>
          <Text strong>Your message:</Text> "{originalMessage}"
        </Paragraph>
        
        <div style={{ marginBottom: 16 }}>
          <Text>I think you want to </Text>
          <Tag color={getActionColor(intentMatch.action)} style={{ margin: '0 4px' }}>
            {intentMatch.action}
          </Tag>
          <Text> a </Text>
          <Tag color={getAreaColor(intentMatch.area)} style={{ margin: '0 4px' }}>
            {intentMatch.area}
          </Tag>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Space wrap>
            <Text type="secondary">Confidence:</Text>
            <Tag 
              color={getConfidenceColor(intentMatch.confidence)}
              style={{ fontWeight: 'bold' }}
            >
              {formatConfidence(intentMatch.confidence)}
            </Tag>
            
            {intentMatch.matched_keywords && intentMatch.matched_keywords.length > 0 && (
              <>
                <Text type="secondary">Matched words:</Text>
                {intentMatch.matched_keywords.slice(0, 3).map((keyword, index) => (
                  <Tag key={index} size="small">
                    {keyword.replace('phrase:', '')}
                  </Tag>
                ))}
                {intentMatch.matched_keywords.length > 3 && (
                  <Tag size="small">+{intentMatch.matched_keywords.length - 3} more</Tag>
                )}
              </>
            )}
          </Space>
        </div>

        <div style={{ 
          background: '#f6ffed', 
          border: '1px solid #b7eb8f', 
          borderRadius: 6, 
          padding: 12,
          marginBottom: 16 
        }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            💡 <Text strong>How this helps:</Text> When you confirm, I learn new words and phrases to better understand similar requests in the future. This makes the system faster and more accurate over time.
          </Text>
        </div>

        <Paragraph>
          <Text strong>Is this interpretation correct?</Text>
        </Paragraph>
      </div>
    </Modal>
  );
};

export default IntentConfirmationDialog; 